import { useEffect, useRef, useState } from 'react'
import { supabase, readableError } from '../../lib/supabase'
import { Player } from '../../components/ui'
import { compressAudio, isCompressible, tooBigMessage, mb as size, MAX_BYTES } from '../../lib/compress'
import { analyseFile as analyseMusicality } from '../../lib/musicality'
import { readDuration } from '../../lib/duration'
import { report, reportSilent } from '../../lib/report'

const FN_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/r2-upload-url`

/**
 * A recording — a voice memo or a video of the room singing it.
 *
 * R2 credentials never reach the browser: the edge function mints a short-lived
 * signed PUT and hands back only the object key. The PUT goes straight from the
 * phone to R2, so a 400 MB video never passes through our servers.
 */
export default function MediaUpload({ song, onChange }) {
  const file = useRef(null)
  const xhr = useRef(null)
  const started = useRef(0)
  const [phase, setPhase] = useState(null)   // preparing | sending | finishing
  const [pct, setPct] = useState(0)
  const [sent, setSent] = useState(0)
  const [rate, setRate] = useState(0)
  const [err, setErr] = useState(null)
  const [src, setSrc] = useState(null)
  const [savedBytes, setSaved] = useState(null)

  // Warm the function the moment this step opens. Cold start is ~3.5s against
  // ~0.7s warm, and all of it lands before the progress bar can move — so the
  // user would just be watching nothing happen. OPTIONS returns before any auth
  // or work, so this costs nothing but boots the isolate.
  useEffect(() => { fetch(FN_URL, { method: 'OPTIONS' }).catch(() => {}) }, [])

  useEffect(() => {
    let alive = true
    if (!song.r2_object_key) { setSrc(null); return }
    supabase.functions
      .invoke('r2-playback-url', { body: { song_id: song.id } })
      .then(({ data, error }) => {
        if (!alive) return
        if (error) return setErr('Saved, but playback is unavailable — check the R2 secrets.')
        setSrc(data?.url ?? null)
      })
    return () => { alive = false }
  }, [song.id, song.r2_object_key])

  const put = (url, f, contentType) => new Promise((resolve, reject) => {
    const req = new XMLHttpRequest()
    xhr.current = req
    req.open('PUT', url, true)
    req.setRequestHeader('Content-Type', contentType)
    req.upload.onprogress = (e) => {
      if (!e.lengthComputable) return
      const frac = e.loaded / e.total
      setSent(e.loaded)
      const secs = (Date.now() - started.current) / 1000
      if (secs > 0.4) setRate(e.loaded / secs)
      // Real bytes, shown on a curve that moves briskly early. It never claims
      // more than 96% while bytes are still in flight — the last jump only
      // happens when the PUT genuinely lands.
      setPct(8 + Math.round(Math.pow(frac, 0.72) * 88))
    }
    req.onload = () => (req.status >= 200 && req.status < 300
      ? resolve()
      : reject(new Error(`Upload failed (${req.status})`)))
    req.onerror = () => reject(new Error('Upload failed — check your connection'))
    req.onabort = () => reject(new Error('Upload cancelled'))
    req.send(f)
  })

  const upload = async (original) => {
    if (!original) return
    setErr(null); setSent(0); setRate(0); setPct(0); setSaved(null)

    let f = original

    // Shrink before the network, not after. A 30 MB WAV becomes ~3 MB, which is
    // the difference between an upload that feels broken and one that is done
    // before the writer finishes reading the next question.
    if (isCompressible(f.type, f.name)) {
      setPhase('compressing')
      try {
        const out = await compressAudio(f, setPct)
        if (out.compressed) {
          f = out.file
          setSaved({ from: out.from, to: out.to })
        }
      } catch {
        // An exotic codec the browser can't decode: send the original instead
        // of failing. The size check below still applies.
        f = original
      }
      setPct(0)
    }

    if (f.size > MAX_BYTES) {
      setPhase(null)
      return setErr(tooBigMessage(f))
    }

    // Is it a song, or is it someone talking about one?
    //
    // The writer is told nothing about this either way. A false accusation on
    // an honest upload — a spoken-word piece, a chant, a rap over a sparse beat
    // — costs far more than a moderator spending a minute listening, so a low
    // score routes to moderation quietly and the writer's flow is untouched.
    //
    // Scored on the final file rather than the original: post-compression it is
    // a few MB instead of thirty, so the extra decode is cheap, and it is also
    // the file a moderator will actually hear.
    // Measured on the final file, after any compression, so it describes what
    // was actually stored. Null if the browser can't tell us -- see readDuration.
    const duration = await readDuration(f)

    let musical = null
    try {
      musical = await analyseMusicality(f)
    } catch {
      // Never block an upload on the analyser. Unscored is a state the schema
      // understands (see flag_unscored_on_submit) and silence is not a verdict.
    }

    setPhase('preparing')
    started.current = Date.now()

    // Creep during signing so the bar is alive from the first instant. Capped
    // well below the transfer range — it is a "working" signal, not progress.
    const creep = setInterval(() => setPct((p) => (p < 7 ? p + 1 : p)), 90)

    try {
      const { data, error } = await supabase.functions.invoke('r2-upload-url', {
        body: {
          song_id: song.id,
          content_type: f.type || '', // Safari sends '' for .m4a
          filename: f.name,
          size_bytes: f.size,
        },
      })
      clearInterval(creep)
      if (error) throw error
      if (data?.error) throw new Error(data.error)

      setPhase('sending')
      started.current = Date.now()
      await put(data.upload_url, f, data.content_type)

      setPhase('finishing'); setPct(97)
      const patch = {
        r2_object_key: data.object_key,
        audio_state: 'pending',
        media_mime: data.content_type,
        media_size_bytes: f.size,
        media_filename: f.name,
        duration_seconds: duration,
        // Written even when confident: the schema treats "checked and fine" and
        // "never checked" as different facts, and only one of them is a flag.
        music_confidence: musical ? musical.confidence : null,
        music_checked_at: musical ? new Date().toISOString() : null,
      }
      const { error: e2 } = await supabase.from('songs').update(patch).eq('id', song.id)
      if (e2) throw e2

      // The trigger on music_confidence has already raised the flag by now.
      // This asks a model to actually listen, so the moderator opens a queue
      // item that already has a second opinion on it. Deliberately not awaited
      // and deliberately swallowed — the upload is finished and successful, and
      // a moderation call that fails is not the writer's problem.
      if (musical?.suspect) {
        supabase.functions
          .invoke('moderate-audio', { body: { song_id: song.id } })
          .catch((e) => reportSilent(e, 'moderation', { song_id: song.id }))
      }

      setPct(100)
      onChange(patch)
      await new Promise((r) => setTimeout(r, 350)) // let 100% register
    } catch (e) {
      // The one failure in this file a writer actually feels: their recording
      // did not save. Reported with the shape of the file but never the file.
      report(e, 'media-upload', {
        song_id: song.id,
        phase: phase,
        bytes: f?.size ?? null,
        type: f?.type || null,
        compressed: Boolean(savedBytes),
      })
      setErr(readableError(e))
    } finally {
      clearInterval(creep)
      setPhase(null); setPct(0); setRate(0)
      xhr.current = null
      if (file.current) file.current.value = ''
    }
  }

  const busy = phase !== null
  const label = phase === 'compressing' ? 'Shrinking it so this is quick…'
    : phase === 'preparing' ? 'Getting ready…'
    : phase === 'finishing' ? 'Almost there…'
    : rate > 0
      ? `Uploading — ${size(sent)} at ${(rate / 1024 / 1024).toFixed(1)} MB/s`
      : 'Uploading…'

  return (
    <>
      <label className="label">A recording</label>
      <Player src={src} mime={song.media_mime} duration={song.duration_seconds}
              filename={song.media_filename} />

      <input
        ref={file}
        type="file"
        hidden
        // Broad on purpose: iOS Voice Memos are .m4a and iPhone video is .mov,
        // and Safari won't offer them from a narrow accept list.
        accept="audio/*,video/*,.m4a,.mov,.mp4,.m4v,.caf,.aac,.wav,.mp3,.3gp"
        onChange={(e) => upload(e.target.files?.[0])}
      />

      {busy ? (
        <div className="uploading">
          <div className="small" style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span>{label}</span><span>{pct}%</span>
          </div>
          <div className="uploadbar"><i style={{ width: `${pct}%` }} /></div>
          {phase === 'sending' && (
            <button className="btn-plain" style={{ paddingLeft: 0 }}
                    onClick={() => xhr.current?.abort()}>Cancel</button>
          )}
        </div>
      ) : (
        <button className="btn btn-quiet" onClick={() => file.current?.click()}>
          {song.r2_object_key ? 'Replace the recording' : 'Add a recording'}
        </button>
      )}

      {savedBytes && !busy && (
        <p className="small" style={{ marginTop: 8, color: 'var(--ok)' }}>
          Compressed {size(savedBytes.from)} → {size(savedBytes.to)} before uploading.
        </p>
      )}
      <p className="small" style={{ marginTop: 8 }}>
        A rough voice memo is plenty. Video works too — up to 100 MB.
        {song.media_size_bytes ? ` Current: ${size(song.media_size_bytes)}.` : ''}
      </p>
      {err && <p className="err">{err}</p>}
    </>
  )
}
